import React, { useState } from 'react';
import axios from 'axios';
import { KeenIcon } from '@/components';
import clsx from 'clsx';
import { CandidateDetailsData, candidateFieldConfig } from '@/pages/buddy/types/CandidateDetailsData';
import { formatDate } from '@/pages/buddy/utils/Date';
import { enqueueSnackbar } from 'notistack';

interface ICandidateDetailItem {
    field: string;
    label: string;
    value: string | null;
    editable: boolean;
    type: 'text' | 'date' | 'boolean' | 'array' | 'number';
}

interface ICandidateDetailsProps {
    title: string;
    candidate: CandidateDetailsData;
    candidate_p_id: string;
    className?: string;
}

const CandidateDetails = ({ title, candidate, candidate_p_id, className }: ICandidateDetailsProps) => {
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [details, setDetails] = useState<ICandidateDetailItem[]>(mapCandidateToDetails(candidate));
    const [loading, setLoading] = useState<boolean>(false);

    const API_URL = import.meta.env.VITE_APP_API_URL;

    function mapCandidateToDetails(candidate: CandidateDetailsData): ICandidateDetailItem[] {
        // Use display property to filter visible fields
        return Object.entries(candidateFieldConfig)
            .filter(([, config]) => config.display)
            .map(([key, config]) => ({
                field: key,
                label: config.label,
                value: candidate[key as keyof CandidateDetailsData]?.toString() || null,
                editable: config.editable,
                type: config.type,
            }));
    }

    const handleSaveClick = async () => {
        setLoading(true);

        try {
            const token = localStorage.getItem('authToken');
            const updatedData = details.reduce((acc, detail) => ({ ...acc, [detail.field]: detail.value }), {});

            await axios.put(
                `${API_URL}/candidate/${candidate_p_id}/details`,
                updatedData,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            setIsEditing(false);
            enqueueSnackbar("Candidate details updated successfully.", { variant: 'success' });
        } catch (err: any) {
            enqueueSnackbar(`Error updating candidate details: ${err}`, {
                variant: 'solid',
                state: 'danger',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleEditClick = () => setIsEditing(true);

    const handleCancelClick = () => {
        setDetails(mapCandidateToDetails(candidate));
        setIsEditing(false);
    };

    const handleDetailChange = (value: string, index: number) => {
        const newDetails = [...details];
        newDetails[index] = { ...newDetails[index], value };
        setDetails(newDetails);
    };

    const renderDetailRow = (item: ICandidateDetailItem, index: number) => {
        // Determine if current field should be editable based on config and editing state
        const canEdit = isEditing && item.editable;

        return (
            <tr key={index} className={clsx(isEditing ? 'w-full' : 'grid grid-cols-2 gap-4 w-full')}>
                <td className="text-sm text-gray-600 min-w-36 pb-4 pe-6">{item.label}</td>
                <td className="text-sm text-gray-800 pb-4">
                    {!canEdit ? (
                        <span>
                            {item.type === 'date' && item.value ? (
                                formatDate(item.value)
                            ) : item.type === 'boolean' ? (
                                <label className="switch switch-sm">
                                    <input
                                        type="checkbox"
                                        checked={item.value === 'true'}
                                        readOnly
                                        disabled
                                    />
                                    <span className="slider round"></span>
                                </label>
                            ) : item.type === 'array' ? (
                                item.value?.split(',').join(', ')
                            ) : (
                                item.value || '-'
                            )}
                        </span>
                    ) : item.type === 'boolean' ? (
                        <label className="switch switch-sm">
                            <input
                                type="checkbox"
                                checked={item.value === 'true'}
                                onChange={(e) => handleDetailChange(e.target.checked ? 'true' : 'false', index)}
                            />
                            <span className="slider round"></span>
                        </label>
                    ) : (
                        <input
                            type="text"
                            value={item.value || ''}
                            onChange={(e) => handleDetailChange(e.target.value, index)}
                            className="input input-sm w-full"
                        />
                    )}
                </td>
            </tr>
        );
    };

    return (
        <div className={clsx('card', className)}>
            <div className="card-header flex justify-between items-center">
                <h3 className="card-title">{title}</h3>
                {!isEditing ? (
                    <button className="btn btn-light btn-sm" onClick={handleEditClick}>
                        <KeenIcon icon="notepad-edit" />
                        Edit
                    </button>
                ) : (
                    <div className="flex space-x-2">
                        <button
                            className="btn btn-primary btn-sm"
                            onClick={handleSaveClick}
                            disabled={loading}
                        >
                            <KeenIcon icon="file-up" />
                            {loading ? 'Saving...' : 'Save'}
                        </button>
                        <button
                            className="btn btn-secondary btn-sm"
                            onClick={handleCancelClick}
                            disabled={loading}
                        >
                            <KeenIcon icon="tablet-delete" />
                            Cancel
                        </button>
                    </div>
                )}
            </div>

            <div className="card-body pt-4 pb-2">
                <table className="table-auto w-full">
                    <tbody>
                    {details.map((detail, index) => renderDetailRow(detail, index))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export { CandidateDetails };
